import React, { useEffect, useState, useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import { Container } from '../../../components'
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline'
import {
    AreaConfig,
    ListVendor,
    ServiceContract,
    UnitConfig,
    getScCompoundKeyString,
    ServiceContractCompoundKey,
    parseScCompoundKeyStr,
} from '../../../models'
import { useServiceContract } from '../../../hooks/useServiceContract'
import { toast } from 'react-toastify'
import Button from '@mui/material/Button'

interface InputState {
    [compoundKey: string]: number | null
}
interface Props {
    vendor: ListVendor
    unitConfigs: UnitConfig[]
    areaConfigs: AreaConfig[]
    onDirtyChange?: (dirty: boolean) => void
    saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

export const EditCurrentPricing = (props: Props) => {
    const { vendor, unitConfigs, areaConfigs, onDirtyChange, saveRef } = props
    const { getServiceContractList, setServiceContracts } = useServiceContract()
    const [selectedService, setSelectedService] = useState<number | null>(
        vendor.services[0]?.id || null,
    )
    const [scMap, setScMap] = useState<InputState>({})
    const [initialScMap, setInitialScMap] = useState<InputState>({})

    // Reset selected service when vendor changes
    useEffect(() => {
        const firstServiceId = vendor.services[0]?.id || null
        setSelectedService(firstServiceId)
    }, [vendor.id])

    useEffect(() => {
        getServiceContractList({ params: { vendor: vendor.id } }).then(
            (res) => {
                mapServiceContracts(res.data)
            },
        )
    }, [vendor.id])

    const mapServiceContracts = (serviceContracts: ServiceContract[]) => {
        const mapping: InputState = {}
        serviceContracts.forEach((sc) => {
            mapping[getScCompoundKeyString(sc)] = sc.price
        })
        setScMap(mapping)
        setInitialScMap(mapping)
    }

    const isDirty = useMemo(
        () => JSON.stringify(scMap) !== JSON.stringify(initialScMap),
        [scMap, initialScMap],
    )

    useEffect(() => {
        onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    const updateInputValue = (
        key: ServiceContractCompoundKey,
        value: number,
    ) => {
        setScMap((prev) => {
            return { ...prev, [getScCompoundKeyString(key)]: value }
        })
    }

    // Helper to get area pricing for selected service
    const areaPricing = useMemo(() => {
        if (!selectedService) return []
        return areaConfigs.map((areaConfig) => {
            const key = getScCompoundKeyString({
                serviceId: selectedService,
                vendorId: vendor.id,
                areaConfigId: areaConfig.id,
                unitConfigId: null,
            })
            return {
                id: key,
                area_config: areaConfig,
                price: scMap[key] ?? '',
            }
        })
    }, [selectedService, areaConfigs, vendor.id, scMap])

    // Helper to get unit pricing for selected service
    const unitPricing = useMemo(() => {
        if (!selectedService) return []
        return unitConfigs.map((unitConfig) => {
            const key = getScCompoundKeyString({
                serviceId: selectedService,
                vendorId: vendor.id,
                areaConfigId: null,
                unitConfigId: unitConfig.id,
            })
            return {
                id: key,
                unit_config: unitConfig,
                price: scMap[key] ?? '',
            }
        })
    }, [selectedService, unitConfigs, vendor.id, scMap])

    // Service list for left panel
    const serviceIdList = useMemo(() => vendor.services.map((srv) => srv.id), [
        vendor.services,
    ])

    // Get service name by id
    const getServiceName = (id: number) => {
        return vendor.services.find((srv) => srv.id === id)?.name || ''
    }

    // Completion indicator (copied from StepVendorPrices)
    const getServiceLabel = (serviceId: number) => {
        const denominator = unitConfigs.length + areaConfigs.length
        if (denominator === 0) {
            return <span style={{ marginRight: 8 }}>(n/a)</span>
        }
        let numerator = 0
        areaConfigs.forEach((ac) => {
            const k = getScCompoundKeyString({
                serviceId: serviceId,
                areaConfigId: ac.id,
                unitConfigId: null,
                vendorId: vendor.id,
            })
            const value = scMap[k]
            if (value !== undefined && value !== null && value > 0) {
                numerator += 1
            }
        })
        unitConfigs.forEach((uc) => {
            const k = getScCompoundKeyString({
                serviceId: serviceId,
                areaConfigId: null,
                unitConfigId: uc.id,
                vendorId: vendor.id,
            })
            const value = scMap[k]
            if (value !== undefined && value !== null && value > 0) {
                numerator += 1
            }
        })
        if (numerator === denominator) {
            return (
                <CheckCircleOutlineIcon
                    color="primary"
                    fontSize="small"
                    style={{ marginRight: 8 }}
                />
            )
        }
        return (
            <span style={{ marginRight: 8 }}>
                ({numerator}/{denominator})
            </span>
        )
    }

    const handleSubmit = async () => {
        const serviceContracts: {
            vendor: number
            area_config: number | null
            unit_config: number | null
            service: number
            price: number | null
        }[] = []
        Object.keys(scMap).forEach((compoundKeyStr) => {
            const compoundKey = parseScCompoundKeyStr(compoundKeyStr)
            if (compoundKey === null) {
                return
            }

            serviceContracts.push({
                vendor: compoundKey.vendorId,
                area_config: compoundKey.areaConfigId,
                unit_config: compoundKey.unitConfigId,
                service: compoundKey.serviceId,
                price: scMap[compoundKeyStr],
            })
        })

        await setServiceContracts({
            body: {
                vendor: vendor.id,
                service_contracts: serviceContracts,
            },
        })
        setInitialScMap(scMap)
        toast.success('Service Contracts Saved')
    }

    useEffect(() => {
        if (saveRef) {
            saveRef.current = handleSubmit
        }
        return () => {
            if (saveRef) {
                saveRef.current = null
            }
        }
    }, [saveRef, scMap, vendor.id])

    if (!selectedService) {
        return null
    }

    return (
        <Container>
            <Box display="flex" flexDirection="column" minHeight={400}>
                {/* Main flex row below header */}
                <Box display="flex" flex={1}>
                    {/* Left panel: Services */}
                    <Box
                        minWidth={220}
                        borderRight={1}
                        borderColor="divider"
                        p={2}
                    >
                        <Typography fontWeight="bold" mb={2}>
                            Services
                        </Typography>
                        <List>
                            {serviceIdList.map((serviceId) => (
                                <ListItem key={serviceId} disablePadding>
                                    <ListItemButton
                                        selected={selectedService === serviceId}
                                        onClick={() =>
                                            setSelectedService(serviceId)
                                        }
                                    >
                                        <Typography
                                            fontWeight={
                                                selectedService === serviceId
                                                    ? 'bold'
                                                    : undefined
                                            }
                                            color={
                                                selectedService === serviceId
                                                    ? 'primary'
                                                    : undefined
                                            }
                                            sx={{ flex: 1 }}
                                        >
                                            {getServiceName(serviceId)}
                                        </Typography>
                                        {getServiceLabel(serviceId)}
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit}
                        >
                            Save Changes
                        </Button>
                    </Box>
                    {/* Main content area */}
                    <Box flex={1} p={3}>
                        <Typography fontWeight="bold" fontSize={20} mb={3}>
                            {getServiceName(selectedService)}
                        </Typography>
                        {/* Area (Partial Pricing) */}
                        <Box mb={5}>
                            <Typography fontWeight="bold" mb={1}>
                                Area (Partial Pricing)
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {areaPricing.length === 0 ? (
                                <Typography color="text.secondary">
                                    No area pricing available.
                                </Typography>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={2}>
                                    {areaPricing.map((item) => (
                                        <Box
                                            key={item.id}
                                            minWidth={220}
                                            mb={2}
                                        >
                                            <Typography>
                                                {item.area_config?.name}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={String(item.price ?? '')}
                                                margin="dense"
                                                onChange={(e) =>
                                                    updateInputValue(
                                                        {
                                                            serviceId: selectedService,
                                                            vendorId: vendor.id,
                                                            areaConfigId:
                                                                item.area_config
                                                                    ?.id ??
                                                                null,
                                                            unitConfigId: null,
                                                        },
                                                        Number(e.target.value),
                                                    )
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                        {/* Unit Types (Whole Unit Pricing) */}
                        <Box>
                            <Typography fontWeight="bold" mb={1}>
                                Unit Types (Whole Unit Pricing)
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            {unitPricing.length === 0 ? (
                                <Typography color="text.secondary">
                                    No unit pricing available.
                                </Typography>
                            ) : (
                                <Box display="flex" flexWrap="wrap" gap={2}>
                                    {unitPricing.map((item) => (
                                        <Box
                                            key={item.id}
                                            minWidth={220}
                                            mb={2}
                                        >
                                            <Typography>
                                                {item.unit_config?.name}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                value={String(item.price ?? '')}
                                                margin="dense"
                                                onChange={(e) =>
                                                    updateInputValue(
                                                        {
                                                            serviceId: selectedService,
                                                            vendorId: vendor.id,
                                                            areaConfigId: null,
                                                            unitConfigId:
                                                                item.unit_config
                                                                    ?.id ??
                                                                null,
                                                        },
                                                        Number(e.target.value),
                                                    )
                                                }
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Container>
    )
}
